/**
 * CapexEditor - EV infrastructure capital expenditure input component
 *
 * Categories: Chargers, Electrical Infrastructure, Soft Costs, Vehicles (optional)
 */

import React from 'react';
import {
    Box,
    Typography,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { vortxColors } from '@vortx/shared-ui';

const EV_CATEGORIES = [
    {
        key: 'chargers',
        label: 'Chargers',
        description: 'EVSE hardware (L2 + DCFC)',
        fields: [
            { key: 'equipment', label: 'Equipment' },
            { key: 'installation', label: 'Installation' },
        ],
    },
    {
        key: 'electrical',
        label: 'Electrical Infrastructure',
        description: 'Transformer, panel, trenching, conduit',
        fields: [
            { key: 'transformer', label: 'Transformer' },
            { key: 'panel', label: 'Panel/Switchgear' },
            { key: 'trenching', label: 'Trenching & Conduit' },
        ],
    },
    {
        key: 'soft_costs',
        label: 'Soft Costs',
        description: 'Permits, engineering, project management',
        fields: [
            { key: 'permits', label: 'Permits & Fees' },
            { key: 'engineering', label: 'Engineering & Design' },
            { key: 'project_mgmt', label: 'Project Management' },
        ],
    },
    {
        key: 'vehicles',
        label: 'Vehicle Premium',
        description: 'EV cost premium over ICE equivalent (optional)',
        fields: [
            { key: 'premium', label: 'Per-Vehicle Premium × Fleet' },
        ],
    },
];

function CapexEditor({ capexData, onCapexChange }) {
    const handleChange = (category, fieldKey, value) => {
        const cleaned = value.replace(/,/g, '');
        const numValue = cleaned === '' ? 0 : parseFloat(cleaned);
        if (isNaN(numValue)) return;
        onCapexChange(category, fieldKey, numValue);
    };

    const getCategoryTotal = (catKey) => {
        const cat = EV_CATEGORIES.find(c => c.key === catKey);
        if (!cat) return 0;
        return cat.fields.reduce(
            (sum, f) => sum + (capexData?.[catKey]?.[f.key] || 0),
            0,
        );
    };

    const totalCapex = EV_CATEGORIES.reduce(
        (sum, cat) => sum + getCategoryTotal(cat.key),
        0,
    );

    return (
        <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                Capital Expenditure (CapEx)
            </Typography>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Line Item</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Amount ($)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {EV_CATEGORIES.map((cat) => (
                            cat.fields.map((f, fi) => (
                                <TableRow key={`${cat.key}-${f.key}`}>
                                    {fi === 0 && (
                                        <TableCell
                                            rowSpan={cat.fields.length}
                                            sx={{ verticalAlign: 'top', borderRight: '1px solid', borderRightColor: 'divider' }}
                                        >
                                            <Typography variant="body2" fontWeight={500}>
                                                {cat.label}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {cat.description}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Typography variant="body2">{f.label}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={capexData?.[cat.key]?.[f.key] || ''}
                                            onChange={(e) => handleChange(cat.key, f.key, e.target.value)}
                                            inputProps={{ style: { textAlign: 'right' }, min: 0 }}
                                            sx={{ width: 140 }}
                                            placeholder="0"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        ))}
                        {/* Category subtotals */}
                        {EV_CATEGORIES.map((cat) => {
                            const subtotal = getCategoryTotal(cat.key);
                            if (subtotal === 0) return null;
                            return (
                                <TableRow key={`subtotal-${cat.key}`} sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell colSpan={2}>
                                        <Typography variant="body2" fontWeight={500}>
                                            {cat.label} Subtotal
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight={500}>
                                            ${subtotal.toLocaleString()}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        <TableRow sx={{ bgcolor: 'action.selected' }}>
                            <TableCell colSpan={2}>
                                <Typography fontWeight={600}>Total CapEx</Typography>
                            </TableCell>
                            <TableCell align="right">
                                <Typography fontWeight={600} sx={{ color: vortxColors.teal }} fontSize="1.1rem">
                                    ${totalCapex.toLocaleString()}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default CapexEditor;
